import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppBaseDataService } from './app-base-data.service';
import { ConnMessageService } from './conn-message.service';
import { ProductsService } from './products.service';

const FINISHED_STATUSES = ['Completed', 'CompletedWithErrors', 'Failed', 'Stalled'];
const POLL_INTERVAL_MS = 4000;
const MAX_POLL_ERRORS = 5;

/**
 * Owns the product import poll so progress and the final result survive leaving the import page.
 * The Products list shows the running job; a toast is shown when the job ends.
 */
@Injectable({ providedIn: 'root' })
export class ImportJobTrackerService {
  /** The job still running, or null. Drives the progress banner. */
  readonly running$ = new BehaviorSubject<any>(null);
  /** Every poll result, including the last one (finished or failed). */
  readonly updates$ = new Subject<any>();
  /** Fires once when a job ends; the Products list reloads on this. */
  readonly finished$ = new Subject<any>();

  private jobId: string = null;
  private timer: any = null;
  private errors = 0;

  constructor(
    private _productsService: ProductsService,
    private _abd: AppBaseDataService,
    private _msg: ConnMessageService
  ) { }

  track(jobId: string, totalRows: number) {
    this.stop();
    this.jobId = jobId;
    this.errors = 0;
    const job = { jobId, status: 'Queued', totalRows, processedRows: 0, percentComplete: 0 };
    this.running$.next(job);
    this.updates$.next(job);
    this.schedule();
  }

  private schedule() {
    this.timer = setTimeout(() => this.poll(), POLL_INTERVAL_MS);
  }

  private poll() {
    const jobId = this.jobId;
    if (!jobId) { return; }
    this._productsService.getImportJob(jobId).subscribe(
      (res: any) => {
        if (jobId !== this.jobId) { return; }
        const j = res && res.data;
        if (!j) { return this.retry(jobId); }
        this.errors = 0;
        this.updates$.next(j);
        if (FINISHED_STATUSES.indexOf(j.status) >= 0) {
          this.finish(j);
        } else {
          this.running$.next(j);
          this.schedule();
        }
      },
      () => this.retry(jobId)
    );
  }

  private retry(jobId: string) {
    if (jobId !== this.jobId) { return; }
    this.errors++;
    if (this.errors >= MAX_POLL_ERRORS) {
      this.fail(jobId, 'Lost contact with the server. Check Products later to see the result.');
      return;
    }
    this.schedule();
  }

  private finish(j: any) {
    this.stop();
    const ok = j.status === 'Completed' || j.status === 'CompletedWithErrors';
    const message = j.status === 'Stalled'
      ? 'The import stopped making progress (usually a server restart). Upload the file again; existing names are skipped.'
      : (j.message || '');
    if (ok) { this._abd.loadInit(); }
    this._msg.showToastMessage(
      (j.status === 'Completed' ? 'Inventory import completed. ' : 'Inventory import failed. ') + message,
      j.status === 'Completed' ? 'success' : 'error');
    this.finished$.next(j);
  }

  private fail(jobId: string, message: string) {
    this.stop();
    const j = { jobId, status: 'Failed', message };
    this.updates$.next(j);
    this._msg.showToastMessage('Inventory import failed. ' + message, 'error');
    this.finished$.next(j);
  }

  private stop() {
    clearTimeout(this.timer);
    this.timer = null;
    this.jobId = null;
    this.running$.next(null);
  }
}
